import React from 'react';
import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './ActivityRow.module.css';
import type { ActivityTone, ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

interface ActivityRowBase {
  /**
   * An `<ActivityGlyph/>`. The row does not choose it, because the same 20px line leads with a
   * lifecycle mark in one artboard and a brand mark in the next.
   *
   * **Pass the size AND the tone the tier expects** — neither can be enforced from here (the mark
   * arrives already rendered, and a glyph paints its own `color`, so it always wins over the row):
   *
   * - `open` / `live` — size 16, at the glyph's own tone. Running's spinner is subtle, Complete's
   *   check-in-circle is subtle, *Fetching context*'s in-card check is muted. `ActivityGlyph`'s
   *   state map already lands all three, so pass no `tone`.
   * - `collapsed` — size 15 **and `tone="hint"`**. The collapsed tier drops its mark to hint along
   *   with its label: conversation.md's icon table draws the leading check of every collapsed/done
   *   StepRow at "15 × 15 · `--color-text-white-hint`" (×3 — the *Founder*, *Designer* and
   *   *Manager* threads), while
   *   `state="done"` derives *muted* (correct for a list row, one step too bright for this one).
   *
   * These are the two numbers a caller can get wrong, so they are restated on every story and on
   * both tiers below.
   */
  icon: React.ReactNode;
  /** The line itself — "Building outline", "Read 2 files", "Thinking". Always one line. */
  label: string;
  /**
   * Recolours the **icon and the label together, never the meta**. The meta is
   * `--cv15-text-hint` in all 80 artboards, including the two where the label is green or red.
   *
   * The icon half is carried by `ActivityGlyph`'s own state map rather than by inheritance: a
   * glyph paints its own `color`, so a passed-in mark always wins over the row. The two agree
   * state for state — `state="accepted"` is `success`, `state="failed"` is `error`, and
   * `rejected` / `deleting` / `deleted` / `stopped` are all `hint`, which is `tone="muted"` here.
   *
   * That agreement is the default and it is right for every artboard the design draws. Where a
   * caller wants this prop to *actually* move both halves — a tone the glyph's own map has no
   * opinion about — hand the mark in as `<ActivityGlyph … tone="inherit"/>`. It then paints no
   * colour of its own, the row's `color` reaches it, and the icon and the label move as one.
   */
  tone?: ActivityTone;
  /**
   * Expand or collapse the body under the row. Supplying it is what turns the row into a real
   * `<button>` — Paper draws a `div`, but the chevron is an affordance and an affordance that
   * cannot be reached from the keyboard is a decoration.
   */
  onToggle?: () => void;
  /**
   * Whether the label carries its own `aria-live="polite"` region. Default `true`.
   *
   * The invariant across `ActivityRow` / `ActivityList` / `ActivityBlock` is: **exactly one live
   * region per announcing subtree, and a standalone row still announces.** A bare row is the
   * standalone case — the specs draw one, this file's own stories draw several — and it has no
   * ancestor to speak for it, so it owns the region by default.
   *
   * Inside `ActivityBlock` the region moves up one level and the block passes `announce={false}`
   * here. That is not a tidy-up: the news in a block is *which child exists* — a skeleton becomes
   * a list, a card grows a pill row, Failed swaps the card for a sentence — and a region scoped to
   * this label cannot see any of it. Keeping both would not double-announce (a mutation is
   * attributed to the nearest ancestor region, so each one is read once), but it leaves two
   * elements claiming one job, and the next reader has to work out which of them is authoritative.
   */
  announce?: boolean;
  theme?: ChatTheme;
  /** Keep a settled row's typography stable when its disclosure body is opened. */
  visual?: 'default' | 'stable';
  /** Animate a short, currently running standalone label without changing its geometry. */
  shimmer?: boolean;
  className?: string;
}

/**
 * A row you can still act on: 16px glyph, 14/20 label, 13/20 meta, chevron-**up** at 15px.
 *
 * Every one of the fourteen `Agent tool` cards, the four in-flight `Agent state` boards, and the
 * open step in each conversation column is this tier.
 */
interface ActivityRowOpenProps extends ActivityRowBase {
  density: 'open';
  /** Right-aligned after the spacer. Absent on Failed and on Interrupted. */
  meta?: string;
  /** The direction is implied by the tier — `up` means "there is a body below, fold it away". */
  disclosure?: 'up' | 'none';
}

/**
 * A row that has finished and folded away: 15px **hint** glyph, 13/20 label, 12/20 meta,
 * chevron-**down** at 14px.
 *
 * Both sizes AND both type steps drop by one — that is the whole difference between the tiers, and
 * it is what lets four settled steps stack above one open one without the column reading as five
 * equal claims on attention.
 */
interface ActivityRowCollapsedProps extends ActivityRowBase {
  density: 'collapsed';
  /** Right-aligned after the spacer. 12/20 here, one step down from the open tier's 13/20. */
  meta?: string;
  /** `down` means "there is a body folded away below, open it". */
  disclosure?: 'down' | 'none';
}

/**
 * The tier that is still moving: `open`'s geometry, plus a separate `…` run and a running clock,
 * and **never** a chevron — there is nothing to fold away from something that has not finished.
 *
 * *Thinking* and the Designer edit-loop thread's "Rewriting artifact 05" are the two the file
 * draws.
 */
interface ActivityRowLiveProps extends ActivityRowBase {
  density: 'live';
  /** The clock — "0:04". 12/20 hint, one step below the open tier's 13/20 meta. */
  elapsed: string;
  /** The live tier's trailing run IS the clock. There is no second meta beside it. */
  meta?: never;
}

export type ActivityRowProps =
  | ActivityRowOpenProps
  | ActivityRowCollapsedProps
  | ActivityRowLiveProps;

const TONE_CLASS: Record<ActivityTone, string> = {
  neutral: styles.toneNeutral,
  muted: styles.toneMuted,
  success: styles.toneSuccess,
  error: styles.toneError,
};

const DENSITY_CLASS = {
  open: styles.open,
  collapsed: styles.collapsed,
  live: styles.live,
} as const;

/**
 * The 20px header line of a tool run, a step, a lifecycle state, or the thinking indicator.
 *
 * The most reused object in v1.5 and the reason this folder has a `Parts` layer at all: six of the
 * nine Paper columns drew this same line under six different names — `ToolRunRow`, `AgentStatusRow`,
 * `StepRow`, `ToolCardHeader`, `ThinkingIndicator` — and every one of them is 20px tall with an 8px
 * gap, a mark, a label, a growing spacer, an optional right-aligned meta, and an optional chevron.
 * Thirty-seven artboards resolve to this component with three values of `density`.
 *
 * What never varies, and is therefore not a prop: the 20px height, the 8px gap, the spacer being a
 * real element rather than `margin-left: auto`, the meta staying `--cv15-text-hint` under every
 * tone, and the chevron's direction (the tier decides it — an expanded row folds up, a folded row
 * opens down).
 */
export function ActivityRow(props: ActivityRowProps) {
  const {
    icon,
    label,
    tone = 'neutral',
    onToggle,
    announce = true,
    theme: themeProp,
    visual = 'default',
    shimmer = false,
    className,
  } = props;
  const theme = useV15Theme(themeProp);
  const { density } = props;
  // Never turn a whole sentence into a loading treatment, or truncate its meaning to fit one.
  const shimmerLabel = (shimmer || density === 'live') && label.trim().length > 0 &&
    label.trim().split(/\s+/u).length <= 3;

  // One trailing run in every tier — the clock on `live`, the meta everywhere else. The union
  // makes the two mutually exclusive so there is never a question of which wins.
  const trailing = density === 'live' ? props.elapsed : props.meta;
  // Defaults to `none`: a chevron promises that something folds away, and the terminal rows
  // (Accepted, Rejected, Deleting, Deleted, Stopped, Thinking) have nothing behind them. A caller
  // who forgets to switch a chevron OFF ships a lie; one who forgets to switch it ON ships a row
  // that is merely quiet.
  const disclosure = density === 'live' ? 'none' : props.disclosure ?? 'none';

  // `onToggle` ALONE decides this, not `onToggle && disclosure` — the two are independent optional
  // props (INVENTORY §1.2 puts `onToggle` on the base and `disclosure` on each density), so a
  // caller may legitimately supply one without the other. Gating on both meant
  // `<ActivityRow density="open" onToggle={fn} />` type-checked, rendered a plain `div`, and threw
  // the callback away with no button, no focus ring and no `aria-expanded` — a silent failure at
  // both the type level and runtime. `disclosure` now governs one thing only: whether the chevron
  // paints. A handed-in handler is always wired, because BUILD-CONTRACT §6 is unconditional:
  // anything clickable is a real `<button>`.
  const interactive = Boolean(onToggle);

  const body = (
    <>
      {icon}
      <span
        className={cn(styles.label, shimmerLabel && styles.labelLive)}
        /* Every tier whose text can change under the reader — `live` and `open` — and only those.
           BUILD-CONTRACT §6 names this exact case ("a tool row going from running to complete"),
           and `open` is the tier that draws it: Running is "Building outline" / "2 of 3" and
           Complete is "Built the outline" / "3 of 3" on the same 20px row, only the
           strings differing. `live` is the same story one step earlier — "Rewriting artifact 05"
           becoming "Rewrote artifact 05".

           A region per row does NOT chatter as a run grows: a polite region announces mutations
           inside ITSELF, and a newly inserted region's initial content is not announced, so adding
           a fifth step never re-reads the four above it.

           `collapsed` is excluded — a folded row is a settled record, not a status in flight. So
           are the clock and the meta, which sit outside the span: nobody needs each passing second
           or each tick of a counter read aloud.

           `announce` is the other way this goes quiet, and it is the one a composite reaches for:
           a row inside `ActivityBlock` is announced by the block instead, because up there the
           label is only one of the things that can change. One region per announcing subtree; a
           standalone row keeps its own. See the prop's TSDoc. */
        aria-live={announce && density !== 'collapsed' ? 'polite' : undefined}
      >
        {label}
      </span>
      {density === 'live' ? (
        /* Its own run rather than a character appended to the label — which is what lets it sit at
           hint while the label sits at muted, and what puts the row's own 8px gap between the word
           and the dots. Purely typographic, so it is hidden from the reading order: "Thinking…" and
           "Thinking" say the same thing out loud.

           THREE NODES, NOT ONE `…`. Paper draws a single ellipsis glyph and that is what this was,
           but the shipping chat's own indicator has always animated three separate dots on a 200ms
           stagger, and Guri asked for the two to be the same thing: *"no need for three dots there,
           it should be okay with all three dots"* — the static character out, the animated three in.
           A single `…` cannot be staggered; the three delays need three boxes. */
        <span className={styles.ellipsis} aria-hidden="true">
          <span className={styles.dot}>.</span>
          <span className={styles.dot}>.</span>
          <span className={styles.dot}>.</span>
        </span>
      ) : null}
      {/* Only when there is something to push apart.

          The spacer exists to hold the label and the meta at opposite ends of the row, and on a row
          with no meta it was holding the label apart from the CHEVRON — which put a lone disclosure
          mark a couple of hundred pixels from the words it discloses, reading as furniture on the
          panel rather than as part of the line. Beka, 2026-09-20, on `Thought for 12 sec`: "put that
          chevron next to the text."

          So a row that carries a meta is unchanged — `1 of 3` and its chevron stay on the right edge
          where the nine column specs draw them, with the 8px the spacer exists to guarantee — and a
          bare row closes up, its chevron one row-gap after the last word. */}
      {trailing ? (
        <>
          <span className={styles.spacer} aria-hidden="true" />
          <span className={styles.meta}>{trailing}</span>
        </>
      ) : null}
      {disclosure !== 'none' ? (
        <span className={styles.chevron} aria-hidden="true">
          <ActivityGlyph
            chrome={disclosure === 'up' ? 'chevron-up' : 'chevron-down'}
            /* 15 up, 14 down. Not a typo in the file — it repeats across all nine columns, and it
               is the same one-step drop the whole collapsed tier takes. */
            size={disclosure === 'up' ? 15 : 14}
            /* The tone stays in `.chevron` rather than moving into this prop, because it is not one
               tone: the mark rests at hint and lifts to muted under the row's own pointer. That is
               a single CSS rule about the row, and `inherit` is what lets the rule reach the glyph
               at all — a mark that paints its own colour cannot be lifted by its parent. */
            tone="inherit"
            theme={theme}
          />
        </span>
      ) : null}
    </>
  );

  const rootClass = cn(
    chatThemeClass(theme),
    styles.root,
    DENSITY_CLASS[density],
    TONE_CLASS[tone],
    visual === 'stable' && styles.stable,
    interactive && styles.interactive,
    className
  );

  if (interactive) {
    return (
      <button
        type="button"
        className={rootClass}
        onClick={onToggle}
        // `open` draws its body; `collapsed` has folded it away. The chevron says the same thing
        // in pixels.
        aria-expanded={density === 'open'}
      >
        {body}
      </button>
    );
  }

  return <div className={rootClass}>{body}</div>;
}
